'use client';

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { orphanOverlayHistory, popOverlayHistory, pushOverlayHistory } from './overlayHistory';

type UseBrowserOverlayHistoryOptions = {
  enabled?: boolean | (() => boolean);
  onClose: () => void;
  open: boolean;
  prefix: string;
};

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export function useBrowserOverlayHistory({ enabled = true, onClose, open, prefix }: UseBrowserOverlayHistoryOptions) {
  const entryIdRef = useRef(`${prefix}-${Math.random().toString(36).slice(2, 11)}`);
  const entryActiveRef = useRef(false);
  const openRef = useRef(open);
  const onCloseRef = useRef(onClose);

  openRef.current = open;
  onCloseRef.current = onClose;

  useIsomorphicLayoutEffect(() => {
    const shouldManageHistory = typeof enabled === 'function' ? enabled() : enabled;
    if (!shouldManageHistory || !open) return;

    const entryId = entryIdRef.current;
    pushOverlayHistory(entryId, () => {
      entryActiveRef.current = false;
      onCloseRef.current();
    });
    entryActiveRef.current = true;

    return () => {
      if (!entryActiveRef.current) return;

      if (openRef.current) {
        orphanOverlayHistory(entryId);
      } else {
        popOverlayHistory(entryId);
      }
      entryActiveRef.current = false;
    };
  }, [enabled, open]);

  return useCallback(() => {
    if (entryActiveRef.current) {
      popOverlayHistory(entryIdRef.current);
      entryActiveRef.current = false;
    }
    onCloseRef.current();
  }, []);
}
